import moment from 'moment';
import { ItemClaimType, PerfectOrder, PerfectOrderItem } from 'src/types/perfectOrder';
import { ItemReturnReason } from 'src/types/itemReturnReasons';

export interface PerfectOrderCompliance {
  disposition: 'Destroy in Field' | 'Return to Warehouse';
  hasSelectedItems: boolean;
  isPickupEligible: boolean;
  isDefectiveCreditEligible: boolean;
  isShortageCreditEligible: boolean;
  reasons: string[];
  totalAmount: number;
}

interface PerfectOrderComplianceInput {
  items?: PerfectOrderItem[];
  totalAmount?: number | null;
  shippedDate?: string | Date | null;
  invoicedDate?: string | Date | null;
  requestDate?: string | Date | null;
  backupWarehouse?: string | null;
}

interface PerfectOrderComplianceItem extends PerfectOrderItem {
  total?: number | null;
  selected?: boolean;
  pendingDelete?: boolean;
}

const defectiveReturnReasonLabels = new Set(['product defective', 'product expired']);

const normalizeRuleLabel = (value?: string): string => value?.trim().toLowerCase() ?? '';

export const isDefectiveClaimType = (claimType?: string): boolean => normalizeRuleLabel(claimType).includes('defective');

export const filterPerfectOrderReturnReasons = (returnReasons: ItemReturnReason[], claimTypes: ItemClaimType[], claimTypeId?: number): ItemReturnReason[] => {
  if (!claimTypeId) {
    return [];
  }

  const selectedClaimType = claimTypes.find((claimType) => claimType.claimTypeId === claimTypeId)?.claimType;

  return returnReasons.filter((reason) => {
    if (reason.isActive === false || reason.claimTypeId !== claimTypeId) {
      return false;
    }

    if (!isDefectiveClaimType(selectedClaimType)) {
      return true;
    }

    return defectiveReturnReasonLabels.has(normalizeRuleLabel(reason.returnReason));
  });
};

// A primaryStockStatus of 'O' (special order) marks an item that must be
// returned to the warehouse; 'S' (stock) does not.
export const isSpecialItem = (item: PerfectOrderItem): boolean => item.primaryStockStatus === 'O';

const specialItemLabel = (item: PerfectOrderItem): string => item.itemNumber ?? `Line ${item.retOrderLine ?? ''}`.trim();

export const buildPerfectOrderComplianceInput = (
  perfectOrder: Pick<PerfectOrder, 'perfectOrderItems' | 'totalAmount' | 'shippedDate' | 'invoicedDate' | 'dateOpened' | 'backupWarehouse'>
): PerfectOrderComplianceInput => {
  const hasItems = perfectOrder.perfectOrderItems?.length > 0;

  return {
    items: perfectOrder.perfectOrderItems,
    totalAmount: hasItems ? calculatePerfectOrderItemsTotal(perfectOrder.perfectOrderItems) : perfectOrder.totalAmount,
    shippedDate: perfectOrder.shippedDate,
    invoicedDate: perfectOrder.invoicedDate,
    requestDate: perfectOrder.dateOpened,
    backupWarehouse: perfectOrder.backupWarehouse,
  };
};

export const calculatePerfectOrderItemsTotal = (items?: PerfectOrderComplianceItem[]): number =>
  (items || [])
    .filter((item) => item.selected !== false && item.pendingDelete !== true)
    .reduce((total, item) => {
      const quantity = Number(item.quantity ?? 0);
      const price = item.customerPrice === null || item.customerPrice === undefined ? Number.NaN : Number(item.customerPrice);

      if (Number.isFinite(quantity) && Number.isFinite(price)) {
        return total + quantity * price;
      }

      const itemTotal = Number(item.total);
      return Number.isFinite(itemTotal) ? total + itemTotal : total;
    }, 0);

export const evaluatePerfectOrderCompliance = ({
  items,
  totalAmount,
  shippedDate,
  invoicedDate,
  requestDate,
  backupWarehouse,
}: PerfectOrderComplianceInput): PerfectOrderCompliance => {
  const reasons: string[] = [];
  const normalizedItems = items || [];
  const selectedItems = normalizedItems.filter((item) => item.selected !== false && item.pendingDelete !== true);
  const hasSelectedItems = selectedItems.length > 0;

  const normalizedTotalAmount = totalAmount ?? calculatePerfectOrderItemsTotal(normalizedItems);
  const specialItems = selectedItems.filter(isSpecialItem);
  const hasSpecialItems = specialItems.length > 0;
  let disposition: PerfectOrderCompliance['disposition'] = 'Return to Warehouse';

  if (!hasSelectedItems) {
    reasons.push('No order items are currently selected. Select at least one item to determine disposition.');
  } else if (normalizedTotalAmount > 0 && normalizedTotalAmount < 500 && !hasSpecialItems) {
    disposition = 'Destroy in Field';
    reasons.push('Total RMA value is less than $500 and contains no special order items.');
  } else if (hasSpecialItems) {
    const labels = [...new Set(specialItems.map(specialItemLabel))];
    reasons.push(`Contains special order item(s) and requires return: ${labels.join(', ')}.`);
  }

  const backupStockedItems = specialItems.filter((item) => item.backupStockStatus === 'S');
  if (backupStockedItems.length > 0) {
    const labels = [...new Set(backupStockedItems.map(specialItemLabel))];
    const warehouseSuffix = backupWarehouse ? ` (${backupWarehouse})` : '';
    reasons.push(`Item(s) ${labels.join(', ')} are special order at the primary warehouse but stocked at the backup warehouse${warehouseSuffix}.`);
  }

  // Dates for eligibility
  const normalizedRequestDate = requestDate ? moment(requestDate) : moment();
  const deliveryDate = shippedDate ? moment(shippedDate) : null;
  const normalizedInvoicedDate = invoicedDate ? moment(invoicedDate) : null;

  const isPickupEligible = !(deliveryDate && normalizedRequestDate.diff(deliveryDate, 'days') > 30);
  const isDefectiveCreditEligible = !(normalizedInvoicedDate && normalizedRequestDate.diff(normalizedInvoicedDate, 'days') > 365);
  const isShortageCreditEligible = !(normalizedInvoicedDate && normalizedRequestDate.diff(normalizedInvoicedDate, 'days') > 90);

  return {
    disposition,
    hasSelectedItems,
    isPickupEligible,
    isDefectiveCreditEligible,
    isShortageCreditEligible,
    reasons,
    totalAmount: normalizedTotalAmount,
  };
};
